import {BigInput} from "../../components/forms/bigInput/BigInput";
import Checkbox from "../../components/forms/checkbox/Checkbox";
import {Button} from "antd";
import React, {useEffect, useRef, useState} from "react";
import {multipleProductVariants, setDiscountForAllShopProducts} from "../../api/setDiscountForAllShopProducts";
import useUserContext from "../../hooks/useUserContext";
import {shopifyIdNumber} from "../../utils/shopifyProductIdHandler";

interface EnableAllCouponsProps {
    onBulkUpdateStart: () => void;
    onBulkUpdateEnd: (discountValue: number, shouldOverwrite: boolean) => void;
}

const EnableAllCoupons = ({ onBulkUpdateStart, onBulkUpdateEnd }: EnableAllCouponsProps) => {
    const { selectedDomain } = useUserContext();
    const [isBulkUpdating, setIsBulkUpdating] = useState(false);
    const [bulkDiscountValue, setBulkDiscountValue] = useState(5);
    const [shouldBulkOverwrite, setShouldBulkOverwrite] = useState(false);
    const [productsUpdatesCount, setProductsUpdateCount] = useState(0);
    const productsUpdatesCountRef = useRef(0);
    const productsMap = useRef(new Map());
    const variantsMap = useRef(new Map());

    const onMultipleCouponApprovals = () => {
        setIsBulkUpdating(true);
    }

    const triggerBulkUpdate = async () => {
        if(!selectedDomain) return;
        const response = await setDiscountForAllShopProducts(selectedDomain, bulkDiscountValue, shouldBulkOverwrite);
        if(response.data.response) {
            onBulkUpdateEnd(bulkDiscountValue, shouldBulkOverwrite);
            variantsMap.current.forEach((variant, key) => {
                const shouldIgnore = variant.selected && !shouldBulkOverwrite && bulkDiscountValue;
                if (shouldIgnore) return;
                variant.id = shopifyIdNumber(variant.id);
                variant.product_id = shopifyIdNumber(variant.product_id);
                const product = productsMap.current.get(variant.product_id);
                product.id = shopifyIdNumber(product.id);
                variantsMap.current.set(`${product.id}-${variant.id}`, {
                    ...variant,
                    selected: !!bulkDiscountValue,
                    initialDiscount: bulkDiscountValue
                });
            });
            productsUpdatesCountRef.current++;
            setProductsUpdateCount(productsUpdatesCountRef.current);
        }
        setTimeout(() => {
            setIsBulkUpdating(false);
        }, 1000);
    }

    useEffect(() => {
        if(isBulkUpdating) {
            onBulkUpdateStart();
            triggerBulkUpdate();
        }
    }, [isBulkUpdating]);

    return <div className="enable-all coupon-bulk-action">
        <div className="section-title">Enable Discounts for All Products with One Click</div>
        <div className="max-discount-field-container">
            <div className="field-label">Discount Rate:</div>
            <div className="max-discount-field">
                <BigInput maxLength={2} value={bulkDiscountValue ? bulkDiscountValue.toString() : '0'} setValue={(newVal: string) => setBulkDiscountValue(newVal ? parseInt(newVal) : 0)} placeholder='Max Discount Allowed' />
                <div className="field-top-layer">{bulkDiscountValue}<span>%</span></div>
            </div>
        </div>

        <div className="coupon-checkbox-container">
            <Checkbox
                isSelected={shouldBulkOverwrite}
                setIsSelected={setShouldBulkOverwrite}
                label="Overwrite Existing Coupon Settings"
                description={`If you already have coupon settings for certain products, enabling this option will overwrite them with the new ${bulkDiscountValue}% discount. Otherwise, the new discount will only apply to products that don't currently have any coupon settings.`}
            />
        </div>

        <Button type="primary" className="enable-all-button" onClick={onMultipleCouponApprovals}>Enable this Discount to All Products</Button>
    </div>
}

export default EnableAllCoupons;